import React, { memo } from 'react';
import { useDispatch } from 'react-redux';
import { workspacePropertySelector } from '../../../redux/selectors/workspace';
import WorkspaceItem from '../WorkspaceItem';
import {
  BeforeCapture,
  DragDropContext,
  Draggable,
  DraggableStateSnapshot,
  DragUpdate,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import * as WorkspaceContentUI from './WorkspaceContentUI';
import { useAppSelector } from '../../../redux/hooks';
import {
  IWorkspaceSliceDraggingProps,
  IWorkspaceSliceRow,
  MAX_ANALYSES_PER_ROW,
  workspaceDragEnd,
  workspaceDragSetSelected,
  workspaceDragStart,
  workspaceDragUpdate,
} from '../../../redux/slices/workspace';

export default memo(function WorkspaceContent() {
  const dispatch = useDispatch();

  const isWorkspaceEditing = useAppSelector((state) =>
    workspacePropertySelector(state, 'isEditing'),
  );
  const isDragging = useAppSelector((state) =>
    workspacePropertySelector(state, 'isDragging'),
  );
  const draggingProps = useAppSelector((state) =>
    workspacePropertySelector(state, 'draggingProps'),
  );
  const selectedElement = useAppSelector((state) =>
    workspacePropertySelector(state, 'selectedElement'),
  );
  const rows: IWorkspaceSliceRow[] = useAppSelector((state) =>
    workspacePropertySelector(state, 'rows'),
  );
  const onDraggableContainerMouseDown = (analysisId: string) => {
    if (!isWorkspaceEditing || selectedElement === analysisId) {
      return;
    }
    dispatch(workspaceDragSetSelected(analysisId));
  };
  const onDragEnd = (result: DropResult) => {
    dispatch(workspaceDragEnd(result));
  };
  const onBeforeCapture = ({ draggableId }: BeforeCapture) => {
    dispatch(workspaceDragStart(draggableId));
  };
  const onDragUpdate = (update: DragUpdate) => {
    dispatch(workspaceDragUpdate(update));
  };

  const getStyle = (style: any, snapshot: DraggableStateSnapshot) => {
    const { isDragging: isSnapshotDragging, isDropAnimating } = snapshot;
    if (!isSnapshotDragging) {
      return {};
    }
    if (!isDropAnimating) {
      return style;
    }
    return {
      ...style,
      // cannot be 0, but make it super tiny
      transitionDuration: `0.001s`,
    };
  };

  const generateClassName = (
    analysisId: string,
    draggingProps: IWorkspaceSliceDraggingProps,
  ) => {
    const { highlightedElement, lastElement, chartsLengthExceeded } =
      draggingProps;
    if (analysisId === highlightedElement) {
      let className = 'highlight';
      if (lastElement) {
        className = 'last-highlight';
      }
      if (chartsLengthExceeded) {
        className = `${className} disabled`;
      }
      return className;
    }
  };

  return (
    <DragDropContext
      onBeforeCapture={onBeforeCapture}
      onDragUpdate={onDragUpdate}
      onDragEnd={onDragEnd}
    >
      {rows.map((row) => (
        <Droppable
          droppableId={row.id}
          direction="horizontal"
          key={row.id}
          isDropDisabled={!isWorkspaceEditing}
        >
          {(provided, snapshot) => (
            <WorkspaceContentUI.Row
              isDragging={isDragging}
              isDraggingOver={snapshot.isDraggingOver}
              shouldShowPlaceholder={
                snapshot.isDraggingOver && row.analysesIds.length === 0
              }
              shouldCollapse={
                !!snapshot.draggingFromThisWith && row.analysesIds.length === 1
              }
              innerRef={provided.innerRef}
              {...provided.droppableProps}
            >
              {row.analysesIds.map((analysisId, analysisIndex) => {
                const className = generateClassName(analysisId, draggingProps);

                const shouldShowLabel =
                  draggingProps.draggableId === analysisId &&
                  (draggingProps.isDropDisabled ||
                    draggingProps.chartsLengthExceeded);

                return (
                  <Draggable
                    draggableId={analysisId}
                    index={analysisIndex}
                    key={analysisId}
                    isDragDisabled={!isWorkspaceEditing}
                  >
                    {(innerProvided, innerSnapshot) => (
                      <WorkspaceContentUI.Container
                        onMouseDown={() =>
                          onDraggableContainerMouseDown(analysisId)
                        }
                        ref={innerProvided.innerRef}
                        {...innerProvided.dragHandleProps}
                        {...innerProvided.draggableProps}
                        style={getStyle(
                          innerProvided.draggableProps.style,
                          innerSnapshot,
                        )}
                        className={className}
                      >
                        <WorkspaceItem
                          analysisId={analysisId}
                          entity={row?.analyses?.[analysisIndex]}
                          isWorkspaceEditing={isWorkspaceEditing}
                          isDragging={innerSnapshot.isDragging}
                          isSelected={
                            selectedElement === analysisId && isWorkspaceEditing
                          }
                        />
                        {shouldShowLabel && (
                          <WorkspaceContentUI.StyledLabel pointing>
                            {`You cannot have more than ${MAX_ANALYSES_PER_ROW} charts on one row.`}
                          </WorkspaceContentUI.StyledLabel>
                        )}
                      </WorkspaceContentUI.Container>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </WorkspaceContentUI.Row>
          )}
        </Droppable>
      ))}
    </DragDropContext>
  );
});
